'use client';

import Link from 'next/link';
import Image from 'next/image';
import {
    Facebook,
    Instagram,
    Linkedin,
    Mail,
    MapPin,
    Phone
} from 'lucide-react';
import styles from './Footer.module.css';

const WhatsAppIcon = ({ size = 24, className }: { size?: number; className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="currentColor"
        className={className}
    >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

export default function Footer() {
    const currentYear = new Date().getFullYear();

    return (
        <footer className={styles.footer}>
            <div className={styles.bgPattern} />

            <div className="container">
                <div className={styles.topSection}>

                    {/* Brand Column */}
                    <div className={styles.brandCol}>
                        <Link href="/" className={styles.logo} aria-label="RotanaBiz Home">
                            <Image
                                src="/rotanabiz-logo-small-optimized.webp"
                                alt="RotanaBiz Logo"
                                width={160}
                                height={45}
                                className={styles.logoImage}
                            />
                        </Link>
                        <p className={styles.tagline}>
                            Your trusted partner for UAE company formation, corporate services, and legal compliance. We simplify business setup so you can focus on growth.
                        </p>

                        <div className={styles.socials}>
                            <a href="https://www.linkedin.com/company/rotana-biz" target="_blank" rel="noopener noreferrer" className={styles.socialIcon} aria-label="LinkedIn">
                                <Linkedin size={18} />
                            </a>
                            <a href="https://www.instagram.com/rotanabiz/" target="_blank" rel="noopener noreferrer" className={styles.socialIcon} aria-label="Instagram">
                                <Instagram size={18} />
                            </a>
                            <a href="https://www.facebook.com/RotanaBiz" target="_blank" rel="noopener noreferrer" className={styles.socialIcon} aria-label="Facebook">
                                <Facebook size={18} />
                            </a>
                        </div>
                    </div>

                    {/* Services Column */}
                    <div>
                        <h4 className={styles.colTitle}>Services</h4>
                        <div className={styles.linkList}>
                            <Link href="/business-setup/freezone" className={styles.footerLink}>Freezone Setup</Link>
                            <Link href="/business-setup/mainland" className={styles.footerLink}>Mainland Setup</Link>
                            <Link href="/business-setup/offshore" className={styles.footerLink}>Offshore Company</Link>
                            <Link href="/services/pro" className={styles.footerLink}>Visa Services</Link>
                            <Link href="/services/bank-account-opening-in-uae" className={styles.footerLink}>Bank Account Opening</Link>
                        </div>
                    </div>

                    <div>
                        <h4 className={styles.colTitle}>Company</h4>
                        <div className={styles.linkList}>
                            <Link href="/about" className={styles.footerLink}>About Us</Link>
                            <Link href="/contact" className={styles.footerLink}>Contact Us</Link>
                            <Link href="/blog" className={styles.footerLink}>Insights & News</Link>
                            <Link href="/business-setup/freezone/business-activities" className={styles.footerLink}>Business Activities</Link>
                            <Link href="/business-setup/freezone/cost-calculator" className={styles.footerLink}>Cost Calculator</Link>
                        </div>
                    </div>

                    {/* Contact Column */}
                    <div>
                        <h4 className={styles.colTitle}>Contact</h4>
                        <div className={styles.linkList}>
                            <a href="https://www.google.com/maps/search/?api=1&query=Julphar,+Al+Mataf+Road,+Ras+Al+Khaimah,+UAE" target="_blank" rel="noopener noreferrer" className={styles.contactItem}>
                                <MapPin size={18} className={styles.contactIcon} />
                                <span>Julphar, Al Mataf Road,<br />Ras Al Khaimah, UAE</span>
                            </a>
                            <a href="tel:+97172430438" className={styles.contactItem}>
                                <Phone size={18} className={styles.contactIcon} />
                                <span>+971 7 2430438</span>
                            </a>
                            <a href="https://wa.me/971588181824" target="_blank" rel="noopener noreferrer" className={styles.contactItem}>
                                <WhatsAppIcon size={18} className={styles.contactIcon} />
                                <span>+971 58 818 1824</span>
                            </a>
                            <a href="mailto:info@rotanabiz.com" className={styles.contactItem}>
                                <Mail size={18} className={styles.contactIcon} />
                                <span>info@rotanabiz.com</span>
                            </a>
                        </div>
                    </div>

                </div>

                {/* Bottom Bar */}
                <div className={styles.bottomBar}>
                    <p className={styles.copyright}>
                        &copy; {currentYear} RotanaBiz Corporate Services. All rights reserved.
                    </p>
                    <p className={styles.creditText}>
                        Made with <span style={{ color: '#e25555' }}>♥</span> in UAE by <a href="https://easynexis.com" target="_blank" className={styles.creditLink}>EasyNexis</a>
                    </p>
                    <div className={styles.legalLinks}>
                        <Link href="/privacy-policy" className={styles.legalLink}>Privacy Policy</Link>
                        <Link href="/terms-of-service" className={styles.legalLink}>Terms of Service</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
